/**
 * API client Textprint — Boutique e-shop (Phase 2 — Sprint 1).
 * Endpoints /v1/operations/textprint/boutique/*
 *
 * 10 sous-modules : Familles · Sous-catégories · Produits · Variantes · Stocks ·
 *                   Mouvements · Remises volume · Clients · Adresses · Fichiers.
 */
import { apiClient, unwrap, unwrapPaginated } from './client';
import type { Paginated } from './operations';
import type {
  FamilleBoutique, SousCategorieBoutique, ProduitBoutique, VarianteBoutique,
  StockBoutique, MouvementStockBoutique, RemiseVolumeBoutique,
  ClientBoutique, AdresseClientBoutique, FichierClientBoutique,
  TypeMouvementBoutique, TypeClientBoutique, MethodePaiementBoutique,
  TypeAdresseBoutique, UniteVenteBoutique,
} from '@/types';

// ── Pagination commune ────────────────────────────────────────────────────
interface BasePaging {
  page?:   number;
  limit?:  number;
  search?: string;
}

// ══════════════════════════════════════════════════════════════════════════
// FAMILLES
// ══════════════════════════════════════════════════════════════════════════

export interface FamilleListParams extends BasePaging {
  actif?: boolean;
}

export interface CreateFamillePayload {
  code:     string;
  nom:      string;
  ordre?:   number;
  iconUrl?: string;
  actif?:   boolean;
}
export type UpdateFamillePayload = Partial<CreateFamillePayload>;

export const boutiqueFamillesApi = {
  list: async (params: FamilleListParams = {}): Promise<Paginated<FamilleBoutique>> => {
    const r = await apiClient.get('/operations/textprint/boutique/familles', { params });
    return unwrapPaginated<FamilleBoutique>(r);
  },
  get: async (id: string): Promise<FamilleBoutique> => {
    const r = await apiClient.get(`/operations/textprint/boutique/familles/${id}`);
    return unwrap<FamilleBoutique>(r);
  },
  create: async (payload: CreateFamillePayload): Promise<FamilleBoutique> => {
    const r = await apiClient.post('/operations/textprint/boutique/familles', payload);
    return unwrap<FamilleBoutique>(r);
  },
  update: async (id: string, payload: UpdateFamillePayload): Promise<FamilleBoutique> => {
    const r = await apiClient.patch(`/operations/textprint/boutique/familles/${id}`, payload);
    return unwrap<FamilleBoutique>(r);
  },
  remove: async (id: string): Promise<void> => {
    await apiClient.delete(`/operations/textprint/boutique/familles/${id}`);
  },
};

// ══════════════════════════════════════════════════════════════════════════
// SOUS-CATÉGORIES
// ══════════════════════════════════════════════════════════════════════════

export interface SousCategorieListParams extends BasePaging {
  familleId?: string;
  actif?:     boolean;
}

export interface CreateSousCategoriePayload {
  familleId: string;
  code:      string;
  nom:       string;
  ordre?:    number;
  actif?:    boolean;
}
export type UpdateSousCategoriePayload = Partial<CreateSousCategoriePayload>;

export const boutiqueSousCategoriesApi = {
  list: async (params: SousCategorieListParams = {}): Promise<Paginated<SousCategorieBoutique>> => {
    const r = await apiClient.get('/operations/textprint/boutique/sous-categories', { params });
    return unwrapPaginated<SousCategorieBoutique>(r);
  },
  get: async (id: string): Promise<SousCategorieBoutique> => {
    const r = await apiClient.get(`/operations/textprint/boutique/sous-categories/${id}`);
    return unwrap<SousCategorieBoutique>(r);
  },
  create: async (payload: CreateSousCategoriePayload): Promise<SousCategorieBoutique> => {
    const r = await apiClient.post('/operations/textprint/boutique/sous-categories', payload);
    return unwrap<SousCategorieBoutique>(r);
  },
  update: async (id: string, payload: UpdateSousCategoriePayload): Promise<SousCategorieBoutique> => {
    const r = await apiClient.patch(`/operations/textprint/boutique/sous-categories/${id}`, payload);
    return unwrap<SousCategorieBoutique>(r);
  },
  remove: async (id: string): Promise<void> => {
    await apiClient.delete(`/operations/textprint/boutique/sous-categories/${id}`);
  },
};

// ══════════════════════════════════════════════════════════════════════════
// PRODUITS
// ══════════════════════════════════════════════════════════════════════════

export interface ProduitListParams extends BasePaging {
  familleId?:        string;
  sousCategorieId?:  string;
  actif?:            boolean;
  featured?:         boolean;
  personnalisable?:  boolean;
}

export interface CreateProduitPayload {
  sku:                  string;
  familleId:            string;
  sousCategorieId?:     string;
  nom:                  string;
  description?:         string;
  marque?:              string;
  refConstructeur?:     string;
  compatibilites?:      string[];
  photos?:              string[];
  prixUnitaireHtCents:  number;
  tvaPct?:              number;
  stockMinimum?:        number;
  unite?:               UniteVenteBoutique;
  poidsKg?:             number;
  personnalisable?:     boolean;
  optionsPerso?:        Record<string, unknown>;
  quantiteMinimum?:     number;
  delaiProductionJours?: number;
  prixSetupCents?:      number;
  actif?:               boolean;
  featured?:            boolean;
}
export type UpdateProduitPayload = Partial<CreateProduitPayload>;

export const boutiqueProduitsApi = {
  list: async (params: ProduitListParams = {}): Promise<Paginated<ProduitBoutique>> => {
    const r = await apiClient.get('/operations/textprint/boutique/produits', { params });
    return unwrapPaginated<ProduitBoutique>(r);
  },
  get: async (id: string): Promise<ProduitBoutique> => {
    const r = await apiClient.get(`/operations/textprint/boutique/produits/${id}`);
    return unwrap<ProduitBoutique>(r);
  },
  create: async (payload: CreateProduitPayload): Promise<ProduitBoutique> => {
    const r = await apiClient.post('/operations/textprint/boutique/produits', payload);
    return unwrap<ProduitBoutique>(r);
  },
  update: async (id: string, payload: UpdateProduitPayload): Promise<ProduitBoutique> => {
    const r = await apiClient.patch(`/operations/textprint/boutique/produits/${id}`, payload);
    return unwrap<ProduitBoutique>(r);
  },
  remove: async (id: string): Promise<void> => {
    await apiClient.delete(`/operations/textprint/boutique/produits/${id}`);
  },
};

// ══════════════════════════════════════════════════════════════════════════
// VARIANTES
// ══════════════════════════════════════════════════════════════════════════

export interface VarianteListParams extends BasePaging {
  produitId?: string;
  actif?:     boolean;
}

export interface CreateVariantePayload {
  produitId:     string;
  skuVariante:   string;
  taille?:       string;
  couleur?:      string;
  prixVarCents?: number;
  actif?:        boolean;
}
export type UpdateVariantePayload = Partial<CreateVariantePayload>;

export const boutiqueVariantesApi = {
  list: async (params: VarianteListParams = {}): Promise<Paginated<VarianteBoutique>> => {
    const r = await apiClient.get('/operations/textprint/boutique/variantes', { params });
    return unwrapPaginated<VarianteBoutique>(r);
  },
  get: async (id: string): Promise<VarianteBoutique> => {
    const r = await apiClient.get(`/operations/textprint/boutique/variantes/${id}`);
    return unwrap<VarianteBoutique>(r);
  },
  create: async (payload: CreateVariantePayload): Promise<VarianteBoutique> => {
    const r = await apiClient.post('/operations/textprint/boutique/variantes', payload);
    return unwrap<VarianteBoutique>(r);
  },
  update: async (id: string, payload: UpdateVariantePayload): Promise<VarianteBoutique> => {
    const r = await apiClient.patch(`/operations/textprint/boutique/variantes/${id}`, payload);
    return unwrap<VarianteBoutique>(r);
  },
  remove: async (id: string): Promise<void> => {
    await apiClient.delete(`/operations/textprint/boutique/variantes/${id}`);
  },
};

// ══════════════════════════════════════════════════════════════════════════
// STOCKS
// ══════════════════════════════════════════════════════════════════════════

export interface StockListParams extends BasePaging {
  produitId?:   string;
  varianteId?:  string;
  sousAlerte?:  boolean;
}

export interface CreateStockPayload {
  produitId:         string;
  varianteId?:       string;
  quantiteActuelle?: number;
  quantiteReservee?: number;
}
export interface UpdateStockPayload {
  quantiteActuelle?: number;
  quantiteReservee?: number;
}

export interface AjusterStockPayload {
  delta:      number;
  motif:      string;
  type?:      TypeMouvementBoutique;
  reference?: string;
}

export const boutiqueStocksApi = {
  list: async (params: StockListParams = {}): Promise<Paginated<StockBoutique>> => {
    const r = await apiClient.get('/operations/textprint/boutique/stocks', { params });
    return unwrapPaginated<StockBoutique>(r);
  },
  get: async (id: string): Promise<StockBoutique> => {
    const r = await apiClient.get(`/operations/textprint/boutique/stocks/${id}`);
    return unwrap<StockBoutique>(r);
  },
  create: async (payload: CreateStockPayload): Promise<StockBoutique> => {
    const r = await apiClient.post('/operations/textprint/boutique/stocks', payload);
    return unwrap<StockBoutique>(r);
  },
  update: async (id: string, payload: UpdateStockPayload): Promise<StockBoutique> => {
    const r = await apiClient.patch(`/operations/textprint/boutique/stocks/${id}`, payload);
    return unwrap<StockBoutique>(r);
  },
  ajusterStock: async (id: string, payload: AjusterStockPayload): Promise<StockBoutique> => {
    const r = await apiClient.post(`/operations/textprint/boutique/stocks/${id}/ajuster`, payload);
    return unwrap<StockBoutique>(r);
  },
  remove: async (id: string): Promise<void> => {
    await apiClient.delete(`/operations/textprint/boutique/stocks/${id}`);
  },
};

// ══════════════════════════════════════════════════════════════════════════
// MOUVEMENTS (audit append-only ; pas de create/update — delete admin only)
// ══════════════════════════════════════════════════════════════════════════

export interface MouvementListParams extends BasePaging {
  produitId?:  string;
  varianteId?: string;
  type?:       TypeMouvementBoutique;
  userId?:     string;
  from?:       string;
  to?:         string;
}

export const boutiqueMouvementsApi = {
  list: async (params: MouvementListParams = {}): Promise<Paginated<MouvementStockBoutique>> => {
    const r = await apiClient.get('/operations/textprint/boutique/mouvements', { params });
    return unwrapPaginated<MouvementStockBoutique>(r);
  },
  get: async (id: string): Promise<MouvementStockBoutique> => {
    const r = await apiClient.get(`/operations/textprint/boutique/mouvements/${id}`);
    return unwrap<MouvementStockBoutique>(r);
  },
  remove: async (id: string): Promise<void> => {
    await apiClient.delete(`/operations/textprint/boutique/mouvements/${id}`);
  },
};

// ══════════════════════════════════════════════════════════════════════════
// REMISES VOLUME
// ══════════════════════════════════════════════════════════════════════════

export interface RemiseVolumeListParams extends BasePaging {
  produitId?: string;
}

export interface CreateRemiseVolumePayload {
  produitId:   string;
  quantiteMin: number;
  remisePct:   number;
}
export type UpdateRemiseVolumePayload = Partial<CreateRemiseVolumePayload>;

export const boutiqueRemisesVolumeApi = {
  list: async (params: RemiseVolumeListParams = {}): Promise<Paginated<RemiseVolumeBoutique>> => {
    const r = await apiClient.get('/operations/textprint/boutique/remises-volume', { params });
    return unwrapPaginated<RemiseVolumeBoutique>(r);
  },
  get: async (id: string): Promise<RemiseVolumeBoutique> => {
    const r = await apiClient.get(`/operations/textprint/boutique/remises-volume/${id}`);
    return unwrap<RemiseVolumeBoutique>(r);
  },
  create: async (payload: CreateRemiseVolumePayload): Promise<RemiseVolumeBoutique> => {
    const r = await apiClient.post('/operations/textprint/boutique/remises-volume', payload);
    return unwrap<RemiseVolumeBoutique>(r);
  },
  update: async (id: string, payload: UpdateRemiseVolumePayload): Promise<RemiseVolumeBoutique> => {
    const r = await apiClient.patch(`/operations/textprint/boutique/remises-volume/${id}`, payload);
    return unwrap<RemiseVolumeBoutique>(r);
  },
  remove: async (id: string): Promise<void> => {
    await apiClient.delete(`/operations/textprint/boutique/remises-volume/${id}`);
  },
};

// ══════════════════════════════════════════════════════════════════════════
// CLIENTS
// ══════════════════════════════════════════════════════════════════════════

export interface ClientListParams extends BasePaging {
  type?:  TypeClientBoutique;
  actif?: boolean;
}

export interface CreateClientPayload {
  contactId?:           string;
  type?:                TypeClientBoutique;
  email:                string;
  phone?:               string;
  nomComplet:           string;
  raisonSociale?:       string;
  remisePermanentePct?: number;
  methodePaiementPref?: MethodePaiementBoutique;
  actif?:               boolean;
}
export type UpdateClientPayload = Partial<CreateClientPayload>;

export const boutiqueClientsApi = {
  list: async (params: ClientListParams = {}): Promise<Paginated<ClientBoutique>> => {
    const r = await apiClient.get('/operations/textprint/boutique/clients', { params });
    return unwrapPaginated<ClientBoutique>(r);
  },
  get: async (id: string): Promise<ClientBoutique> => {
    const r = await apiClient.get(`/operations/textprint/boutique/clients/${id}`);
    return unwrap<ClientBoutique>(r);
  },
  create: async (payload: CreateClientPayload): Promise<ClientBoutique> => {
    const r = await apiClient.post('/operations/textprint/boutique/clients', payload);
    return unwrap<ClientBoutique>(r);
  },
  update: async (id: string, payload: UpdateClientPayload): Promise<ClientBoutique> => {
    const r = await apiClient.patch(`/operations/textprint/boutique/clients/${id}`, payload);
    return unwrap<ClientBoutique>(r);
  },
  remove: async (id: string): Promise<void> => {
    await apiClient.delete(`/operations/textprint/boutique/clients/${id}`);
  },
};

// ══════════════════════════════════════════════════════════════════════════
// ADRESSES CLIENT
// ══════════════════════════════════════════════════════════════════════════

export interface AdresseClientListParams extends BasePaging {
  clientId?: string;
  type?:     TypeAdresseBoutique;
}

export interface CreateAdresseClientPayload {
  clientId:    string;
  type?:       TypeAdresseBoutique;
  libelle?:    string;
  ligne1:      string;
  ligne2?:     string;
  ville:       string;
  zone?:       string;
  pays?:       string;
  estDefaut?:  boolean;
}
export type UpdateAdresseClientPayload = Partial<CreateAdresseClientPayload>;

export const boutiqueAdressesClientApi = {
  list: async (params: AdresseClientListParams = {}): Promise<Paginated<AdresseClientBoutique>> => {
    const r = await apiClient.get('/operations/textprint/boutique/adresses-client', { params });
    return unwrapPaginated<AdresseClientBoutique>(r);
  },
  get: async (id: string): Promise<AdresseClientBoutique> => {
    const r = await apiClient.get(`/operations/textprint/boutique/adresses-client/${id}`);
    return unwrap<AdresseClientBoutique>(r);
  },
  create: async (payload: CreateAdresseClientPayload): Promise<AdresseClientBoutique> => {
    const r = await apiClient.post('/operations/textprint/boutique/adresses-client', payload);
    return unwrap<AdresseClientBoutique>(r);
  },
  update: async (id: string, payload: UpdateAdresseClientPayload): Promise<AdresseClientBoutique> => {
    const r = await apiClient.patch(`/operations/textprint/boutique/adresses-client/${id}`, payload);
    return unwrap<AdresseClientBoutique>(r);
  },
  remove: async (id: string): Promise<void> => {
    await apiClient.delete(`/operations/textprint/boutique/adresses-client/${id}`);
  },
};

// ══════════════════════════════════════════════════════════════════════════
// FICHIERS CLIENT
// ══════════════════════════════════════════════════════════════════════════

export interface FichierClientListParams extends BasePaging {
  clientId?:      string;
  formatFichier?: string;
}

export interface CreateFichierClientPayload {
  clientId:       string;
  nom:            string;
  url:            string;
  formatFichier?: string;
  tailleBytes?:   number;
}
export type UpdateFichierClientPayload = Partial<CreateFichierClientPayload>;

export const boutiqueFichiersClientApi = {
  list: async (params: FichierClientListParams = {}): Promise<Paginated<FichierClientBoutique>> => {
    const r = await apiClient.get('/operations/textprint/boutique/fichiers-client', { params });
    return unwrapPaginated<FichierClientBoutique>(r);
  },
  get: async (id: string): Promise<FichierClientBoutique> => {
    const r = await apiClient.get(`/operations/textprint/boutique/fichiers-client/${id}`);
    return unwrap<FichierClientBoutique>(r);
  },
  create: async (payload: CreateFichierClientPayload): Promise<FichierClientBoutique> => {
    const r = await apiClient.post('/operations/textprint/boutique/fichiers-client', payload);
    return unwrap<FichierClientBoutique>(r);
  },
  update: async (id: string, payload: UpdateFichierClientPayload): Promise<FichierClientBoutique> => {
    const r = await apiClient.patch(`/operations/textprint/boutique/fichiers-client/${id}`, payload);
    return unwrap<FichierClientBoutique>(r);
  },
  remove: async (id: string): Promise<void> => {
    await apiClient.delete(`/operations/textprint/boutique/fichiers-client/${id}`);
  },
};
